import { ExtractedExpenseData, ReceiptProcessingResult } from '../types/receipt';
import { convertPdfToImage, isPdfFile } from '../utils/pdfToImage';
import { appFirebaseAuth } from '../context/firebase/firebase';

/**
 * Service for processing receipts and extracting expense data using AI
 *
 * Uses OpenAI GPT-4o-mini for receipt OCR and structured data extraction
 * Supports images (PNG, JPG, WEBP, GIF) and PDFs (converts first page to image)
 */
class ReceiptProcessingService {
  /**
   * Process a receipt image or PDF and extract expense data
   * @param fileUrl - URL of the uploaded receipt file
   * @param mimeType - MIME type of the file (optional)
   * @returns Extracted expense data
   */
  async processReceipt(fileUrl: string, mimeType?: string): Promise<ReceiptProcessingResult> {
    try {
      let imageUrl = fileUrl;

      // If it's a PDF, convert the first page to an image
      if (isPdfFile(fileUrl, mimeType)) {
        console.log('Converting PDF to image for AI processing...');
        imageUrl = await convertPdfToImage(fileUrl);
      }

      const extractedData = await this.processWithOpenAI(imageUrl);

      return {
        success: true,
        data: extractedData
      };
    } catch (error) {
      console.error('Error processing receipt:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to process receipt'
      };
    }
  }

  /**
   * Process receipt with OpenAI GPT-4o-mini Vision via Supabase Edge Function
   */
  private async processWithOpenAI(fileUrl: string): Promise<ExtractedExpenseData> {

    const prompt = `Analyze this receipt/invoice image carefully. Extract expense data with tax/tip/fee breakdown. Return ONLY a valid JSON object:

{
  "has_multiple_items": <boolean, true if receipt has multiple distinct expense categories>,
  "merchant": <string, merchant/vendor name>,
  "expense_date": <string, date in YYYY-MM-DD format>,
  "currency": <string, 3-letter code like "USD", "EUR", "CHF">,
  "total_amount": <number, grand total>,
  "items": [
    {
      "base_amount": <number, main expense before tax/tip/fees>,
      "tax": <number or null, sales tax, VAT, city tax, etc.>,
      "tip": <number or null, gratuity/service charge>,
      "fees": <number or null, service fees, booking fees, etc.>,
      "amount": <number, total for this item (base_amount + tax + tip + fees)>,
      "description": <string, what this expense is>,
      "category": <string, from list below>,
      "expense_date": <string, date in YYYY-MM-DD format - FOR FLIGHTS: use actual flight/travel date, NOT invoice date>
    }
  ],
  "confidence": <number, 0 to 1>
}

Valid categories:
- "Flight" - Airplane tickets, airline bookings, flight-related fees
- "Accommodation" - Hotel rooms, lodging, overnight stays
- "Food & Dining" - Restaurant meals, room service, cafe, groceries
- "Transportation" - Trains, taxis, rideshare (Uber/Lyft), car rental, gas, parking, buses (NOT flights)
- "Activities" - Tours, attractions, tickets, admissions
- "Entertainment" - Shows, concerts, nightlife
- "Shopping" - Souvenirs, gifts, purchases
- "Health & Wellness" - Pharmacy, medical, spa
- "Emergency" - Urgent/unexpected expenses
- "Other" - Anything else

CRITICAL Rules:
1. ALWAYS separate base_amount, tax, tip, and fees when visible on receipt
2. City tax / accommodation tax goes in "tax" field, NOT as separate expense
3. If receipt has multiple DISTINCT ITEMS (different categories OR different purchases within same category), set "has_multiple_items": true with separate items
4. Multiple flight legs (different dates/routes) = multiple items even if same category
5. If receipt is ONE SINGLE purchase with tax/tip (one restaurant meal + tax + tip), set "has_multiple_items": false with 1 item
6. Don't create separate line items for tax/tip/fees - use the dedicated fields instead
7. Payment method info (Visa, card number) is NOT an expense - ignore it
8. Zero-amount items on invoices should still be included as separate items
9. Return ONLY JSON, no markdown formatting
10. **FLIGHT DATES**: For flight tickets/invoices, use the ACTUAL FLIGHT DATE from the itinerary (departure date), NOT the invoice issue date or booking date. Each flight leg should have its own expense_date matching the departure date.
11. **MERCHANT-BASED CATEGORY**: If merchant name contains "hotel", "inn", "resort", "hostel", or similar lodging terms, category MUST be "Accommodation". If merchant is an airline (e.g., "Swiss Air", "El Al", "Lufthansa"), category MUST be "Flight".

Example 1 - Hotel invoice with room + tax:
{
  "has_multiple_items": false,
  "merchant": "Hotel Ameron",
  "expense_date": "2025-10-05",
  "currency": "CHF",
  "total_amount": 301.00,
  "items": [
    {
      "base_amount": 297.50,
      "tax": 3.50,
      "tip": null,
      "fees": null,
      "amount": 301.00,
      "description": "Overnight stay",
      "category": "Accommodation"
    }
  ],
  "confidence": 0.95
}

Example 2 - Hotel invoice with room + restaurant (different categories):
{
  "has_multiple_items": true,
  "merchant": "Hotel Ameron",
  "expense_date": "2025-10-05",
  "currency": "CHF",
  "total_amount": 319.00,
  "items": [
    {"base_amount": 297.50, "tax": 3.50, "tip": null, "fees": null, "amount": 301.00, "description": "Overnight stay", "category": "Accommodation"},
    {"base_amount": 16.20, "tax": 1.80, "tip": null, "fees": null, "amount": 18.00, "description": "Restaurant food", "category": "Food & Dining"}
  ],
  "confidence": 0.95
}

Example 3 - Restaurant with tip (single purchase):
{
  "has_multiple_items": false,
  "merchant": "Cafe Bistro",
  "expense_date": "2025-10-06",
  "currency": "USD",
  "total_amount": 57.50,
  "items": [
    {"base_amount": 45.00, "tax": 4.50, "tip": 8.00, "fees": null, "amount": 57.50, "description": "Lunch", "category": "Food & Dining"}
  ],
  "confidence": 0.9
}

Example 4 - Flight invoice with multiple legs (same category, different purchases):
{
  "has_multiple_items": true,
  "merchant": "Airline",
  "expense_date": "2025-10-05",
  "currency": "USD",
  "total_amount": 3218.00,
  "items": [
    {"base_amount": 3218.00, "tax": 0.00, "tip": null, "fees": null, "amount": 3218.00, "description": "Tel Aviv to Zurich", "category": "Flight", "expense_date": "2025-10-05"},
    {"base_amount": 0.00, "tax": 0.00, "tip": null, "fees": null, "amount": 0.00, "description": "Zurich to Frankfurt", "category": "Flight", "expense_date": "2025-10-06"},
    {"base_amount": 0.00, "tax": 0.00, "tip": null, "fees": null, "amount": 0.00, "description": "Frankfurt to Tel Aviv", "category": "Flight", "expense_date": "2025-10-08"}
  ],
  "confidence": 0.95
}`;

    // Get Firebase auth token to pass to edge function
    const user = appFirebaseAuth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated');
    }

    const token = await user.getIdToken();

    // Get Supabase URL
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('Supabase URL not configured');
    }

    // Call edge function directly with fetch for full header control
    const functionUrl = `${supabaseUrl}/functions/v1/process-receipt-with-ai`;

    console.log('Calling edge function with Authorization header');

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        fileUrl,
        model: 'gpt-4o-mini',
        prompt
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Edge function error response:', errorText);
      throw new Error(`Failed to process receipt with AI: ${response.status} ${errorText}`);
    }

    const data = await response.json();

    console.log('Edge function response:', data);

    if (!data.success) {
      const errorMessage = data.error || data.message || 'Unknown error';
      console.error('Edge function returned error:', errorMessage);
      throw new Error(`Failed to process receipt with AI: ${errorMessage}`);
    }

    const content = data.content;

    // Remove markdown code blocks if present
    const jsonContent = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();

    const parsed = JSON.parse(jsonContent);

    // Handle new multi-item format with tax/tip/fee support
    if (parsed.has_multiple_items && parsed.items && parsed.items.length > 1) {
      // Multiple line items - return structured data
      return {
        has_multiple_items: true,
        merchant: parsed.merchant,
        expense_date: parsed.expense_date,
        currency: parsed.currency || 'USD',
        total_amount: parsed.total_amount,
        items: parsed.items.map((item: any) => ({
          amount: item.amount,
          base_amount: item.base_amount || null,
          tax: item.tax || null,
          tip: item.tip || null,
          fees: item.fees || null,
          currency: parsed.currency || 'USD',
          description: item.description,
          category: item.category,
          expense_date: item.expense_date || parsed.expense_date, // Use item-specific date if available (e.g., for flights)
          merchant: parsed.merchant
        })),
        confidence: parsed.confidence || 0.5
      };
    } else {
      // Single item - return in legacy format for compatibility
      const singleItem = parsed.items?.[0] || {};
      return {
        has_multiple_items: false,
        amount: singleItem.amount || parsed.total_amount || null,
        base_amount: singleItem.base_amount || null,
        tax: singleItem.tax || null,
        tip: singleItem.tip || null,
        fees: singleItem.fees || null,
        currency: parsed.currency || 'USD',
        description: singleItem.description || null,
        category: singleItem.category || null,
        expense_date: singleItem.expense_date || parsed.expense_date || null, // Use item-specific date if available
        merchant: parsed.merchant || null,
        confidence: parsed.confidence || 0.5
      };
    }
  }

}

export const receiptProcessingService = new ReceiptProcessingService();
